import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import PortalShell from "@/components/PortalShell";
import { GraduationCap, Trophy, Award, CheckCircle2, Circle, Clock, Play, ShieldCheck } from "lucide-react";

function BadgeIcon({ id }) {
  if (id === "excellence") return <Trophy className="w-5 h-5"/>;
  if (id === "compliance") return <ShieldCheck className="w-5 h-5"/>;
  return <Award className="w-5 h-5"/>;
}

export default function Academy() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/academy/dashboard");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (id) => {
    setBusy(true);
    try {
      await api.post(`/academy/checklist/${id}/toggle`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startOrComplete = async (module) => {
    const next = module.progress >= 100 ? 100 : Math.min(100, module.progress + 50);
    setBusy(true);
    try {
      await api.post("/academy/progress", { module_id: module.id, progress: next });
      toast.success(next === 100 ? "Module completed" : "Progress saved (50%)");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <PortalShell variant="employee">
        <div className="text-slate-500 text-sm" data-testid="academy-loading">Loading your Academy…</div>
      </PortalShell>
    );
  }

  return (
    <PortalShell variant="employee">
      <Toaster richColors position="top-right" />

      {/* Welcome */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <span className="wc-eyebrow">WeCare II Academy</span>
          <h1 className="mt-3 font-outfit text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
            Welcome, {data.user.name.split(" ")[0]}.
          </h1>
          <p className="mt-3 text-slate-600 text-sm max-w-lg">Complete your onboarding, training, and quizzes to unlock badges and certificates.</p>
        </div>
        <div className="wc-card p-6 min-w-[240px]" data-testid="overall-progress-card">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Overall progress</div>
          <div className="mt-2 flex items-end gap-2">
            <div className="font-outfit text-4xl font-semibold text-slate-900">{data.overall_progress}%</div>
            <div className="text-xs text-slate-500 mb-1">{data.completed_count}/{data.total_count} modules</div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-sky-100 overflow-hidden">
            <div className="h-full bg-sky-500 rounded-full" style={{ width: `${data.overall_progress}%`, transition: "width 400ms ease" }} />
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.badges.map((b) => (
          <div key={b.id} className={`wc-card p-6 flex items-center gap-4 ${b.earned ? "" : "opacity-60"}`} data-testid={`badge-${b.id}`}>
            <div className={`w-12 h-12 rounded-2xl grid place-items-center ${b.earned ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}>
              <BadgeIcon id={b.id}/>
            </div>
            <div>
              <div className="font-outfit font-semibold text-slate-900">{b.name}</div>
              <div className="text-xs text-slate-500">{b.earned ? "Earned" : "Locked"}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Training modules */}
        <div className="lg:col-span-2 wc-card p-6 md:p-8">
          <div className="flex items-center justify-between">
            <h2 className="font-outfit text-xl font-semibold text-slate-900 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-sky-600"/> Training Center</h2>
            <div className="text-xs text-slate-500">{data.modules.length} modules</div>
          </div>
          <div className="mt-6 divide-y divide-slate-100">
            {data.modules.map((m) => (
              <div key={m.id} className="py-5 flex items-center gap-4" data-testid={`module-${m.id}`}>
                <div className={`w-11 h-11 rounded-xl grid place-items-center ${m.completed ? "bg-emerald-50 text-emerald-600" : "bg-slate-50 text-slate-500"}`}>
                  {m.completed ? <CheckCircle2 className="w-5 h-5"/> : <Play className="w-5 h-5"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{m.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2"><Clock className="w-3 h-3"/> {m.duration_min} min · {m.category}</div>
                  <div className="mt-2 h-1.5 rounded-full bg-sky-100 overflow-hidden max-w-md">
                    <div className="h-full bg-sky-500 rounded-full" style={{ width: `${m.progress}%`, transition: "width 300ms ease" }}/>
                  </div>
                </div>
                <button
                  data-testid={`module-action-${m.id}`}
                  disabled={busy || m.completed}
                  onClick={() => startOrComplete(m)}
                  className={m.completed ? "text-emerald-600 text-xs font-semibold" : "wc-btn-outline !py-2 !px-4 text-xs"}
                >
                  {m.completed ? "Completed" : m.progress > 0 ? "Continue" : "Start"}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <div className="wc-card p-6" data-testid="checklist-card">
            <h3 className="font-outfit text-lg font-semibold text-slate-900">New Hire Checklist</h3>
            <ul className="mt-4 space-y-3">
              {data.checklist.map((c) => (
                <li key={c.id} className="flex items-start gap-3">
                  <button data-testid={`checklist-${c.id}`} onClick={() => toggle(c.id)} disabled={busy}
                    className={`mt-0.5 w-5 h-5 rounded-full grid place-items-center ${c.done ? "bg-emerald-500 text-white" : "border border-slate-300 text-transparent"}`}>
                    {c.done ? <CheckCircle2 className="w-3.5 h-3.5"/> : <Circle className="w-3.5 h-3.5"/>}
                  </button>
                  <div className={`text-sm ${c.done ? "line-through text-slate-400" : "text-slate-700"}`}>{c.title}</div>
                </li>
              ))}
            </ul>
          </div>

          <div className="wc-card p-6" data-testid="announcements-card">
            <h3 className="font-outfit text-lg font-semibold text-slate-900">Announcements</h3>
            <div className="mt-4 space-y-4">
              {data.announcements.length === 0 && <div className="text-sm text-slate-500">No announcements yet.</div>}
              {data.announcements.map((a) => (
                <div key={a.id}>
                  <div className="text-sm font-semibold text-slate-900">{a.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{new Date(a.created_at).toLocaleDateString()} · {a.author_name}</div>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{a.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
